import {
  SET_VIEWS,
  SET_CURRENT_VIEW,
  SET_VIEW_SLOTS,
  SET_ALL_CAMERAS,
  CAM_VIEW_CHANGED,
  SAVE_ORIGINAL_CAMERAS,
  RESTORE_ORIGINAL_CAMERAS,
  ASSIGN_CAMERA_TO_SLOT,
  SET_VIEW_DIRTY,
  CLEAR_VIEW_DIRTY,
  SET_VIEW_PRISTINE,
  SET_MCPB_SLOTS
} from './types';

import axios from 'axios';
import { getApiUrl } from '../util/getServerInfo';
import { debugLog, debugWarn } from '../util/debug';
import { isMultiServerSession } from '../util/sessionMode';
import { getBackend } from '../backend';
import { getVisibleCameras } from '../util/camSets';
import { normalizeSlotCamera } from '../util/cameraDrawer';

// Re-export for tests + back-compat with existing imports.
export { isMultiServerSession };

/**
 * Last Selected View Persistence (mirrors Java dview's CookieModel)
 *
 * Java stores in: ~/.dview-cookie.txt with format: setHash=serial~viewName>serial~viewName
 * We use localStorage with key: dview_last_view_{serial}
 */

/**
 * Save the user's last selected view for a server
 * @param {number} bSerial - Server serial number
 * @param {string} sViewName - View name to save
 */
const saveLastSelectedView = (bSerial, sViewName) => {
  // Need valid serial (not 0, not null/undefined) and view name
  if (!bSerial || bSerial === 0 || !sViewName) {
    debugLog(`[ViewActions] Not saving view - invalid serial (${bSerial}) or view name (${sViewName})`);
    return;
  }

  // Don't save synthetic views
  if (sViewName.startsWith('__synthetic_')) return;

  try {
    const key = `dview_last_view_${bSerial}`;
    localStorage.setItem(key, sViewName);
    debugLog(`[ViewActions] Saved last view to localStorage: server=${bSerial}, view="${sViewName}"`);
  } catch (e) {
    debugWarn('[ViewActions] Failed to save last view to localStorage:', e);
  }
};

/**
 * Load the user's last selected view for a server
 * @param {number} bSerial - Server serial number
 * @returns {string|null} - Saved view name or null
 */
const loadLastSelectedView = (bSerial) => {
  if (!bSerial) return null;

  try {
    const key = `dview_last_view_${bSerial}`;
    const savedView = localStorage.getItem(key);
    if (savedView) {
      debugLog(`[ViewActions] Loaded last view for server ${bSerial}: ${savedView}`);
    }
    return savedView;
  } catch (e) {
    debugWarn('[ViewActions] Failed to load last view from localStorage:', e);
    return null;
  }
};

/**
 * Clear the saved view for a server (e.g., when view is deleted)
 * @param {number} bSerial - Server serial number
 */
export const clearLastSelectedView = (bSerial) => {
  if (!bSerial) return;

  try {
    const key = `dview_last_view_${bSerial}`;
    localStorage.removeItem(key);
    debugLog(`[ViewActions] Cleared last view for server ${bSerial}`);
  } catch (e) {
    debugWarn('[ViewActions] Failed to clear last view from localStorage:', e);
  }
};

/**
 * Fetch all views for the current user
 *
 * In eView mode: Fetches views from ALL connected servers and aggregates them
 * In dview mode: Fetches views from the current server only
 *
 * @param {string} sSess - Session token
 * @param {number} bUserID - User ID
 * @param {boolean} fEview - Is enterprise viewer mode enabled?
 */
// Title-case a name for display ("DINING 1" / "dining 1" → "Dining 1"). Smart-view names are aggregated
// case-insensitively (lowercased), so we normalize the display casing here. #86bb7phhh David.
export const titleCase = (s) => (s || '').toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());

// A member NVR inside a multi-box SITE is named "<Site>-<n>" (Rosa's Cafe #02-1 / #02-2). Strip that
// trailing "-<n>" (hyphen/underscore + digits, optional spaces) to get the site's base name so both
// NVRs collapse to ONE "Rosa's Cafe #02" view in eView. A bare SPACE before the digits is NOT a site
// suffix ("Store 5" is one site, not "Store"), so it's left intact; "#02" (no separator before the
// digits) is left intact too. Only ever applied when ≥2 NVRs share the base. #86bb7phhh David.
export const systemBaseName = (name) => (name || '').replace(/\s*[-_]\s*\d+\s*$/, '').trim();

export const getViews = (sSess, bUserID, fEview) => async (dispatch, getState) => {
  debugLog('[ViewActions] getViews called with:', { sSess, bUserID, fEview });

  const state = getState();
  const localSerial = state.server.bSerial;
  const backend = getBackend(state);
  const isCloud = !!(backend && backend.kind === 'cloud');
  // Cloud rides the eView-style organize path for the two COMPUTED groups: per-"server"
  // Default views (each cloud device is a "server" via bSerial=bID+9999 + sServerName) and
  // cross-device Smart views (same-named cameras across devices). The all-devices camera
  // pool (state.server.allCameras) already carries per-device serials + names, so the exact
  // eView generation runs UNCHANGED for cloud (unified-viewer-cloud-views.md §7/§8). Stored
  // Global/User/fDefault views still come from the DB (getViewSlotRows) and organize as-is.
  const isEviewLike = fEview || isCloud;

  let allViewSlots = [];

  // CLOUD: views come from the CloudNvrAdapter, which maps the login session's cloud
  // views (sCams) into the local getSets row shape (deviceId -> serial = bID+9999). No
  // per-NVR JSON-RPC. The rows then flow through the SAME parse/organize/dispatch body
  // below — cloud rides the cross-device (eView-style) matching via those serials.
  if (isCloud) {
    allViewSlots = backend.getViewSlotRows();
    debugLog('[ViewActions] cloud getViews: got', allViewSlots.length, 'slot rows from adapter');
  } else if (fEview) {
    // eView: fetch views from the CONTROLLER's aggregated getSets (fEview=true) in ONE call. A per-member
    // fan-out is NOT viable in this architecture: members are session-less by design, so their getSets
    // calls come back noauth/errored, and the global session interceptor mistakes that for the CONTROLLER
    // session expiring — tearing the whole eView session down (empty grid, no user rights/username). That
    // is the exact bug ab46c4091 fixed and that this reverts back to. The chain-"smart"-views-as-user-views
    // problem is real but must be solved WITHOUT the fan-out. #86bb7phhh
    debugLog('[ViewActions] eView mode: fetching aggregated views from the controller (single call)...');

    const reqBody = { jsonrpc: "2.0", method: "config.user.getSets", params: [sSess, bUserID, true], id: 300 };

    try {
      const response = await axios.post(getApiUrl(), reqBody);
      if (response.data.error) {
        debugWarn('[ViewActions] eView getViews backend error:', response.data.error);
      } else {
        let result = response.data.result;
        if (Array.isArray(result) && result.length === 2 && (typeof result[0] === 'boolean' || result[0] === 0 || result[0] === 1)) {
          result = result[0] ? result[1] : [];
        }
        if (Array.isArray(result)) {
          allViewSlots = result.map(slot => ({
            ...slot,
            sourceSerial: localSerial,
            sourceName: state.server.sName || 'Controller'
          }));
        }
      }
      debugLog(`[ViewActions] eView: got ${allViewSlots.length} aggregated view slots from the controller`);
    } catch (e) {
      debugWarn('[ViewActions] eView getViews fetch failed:', e.message);
    }
  } else {
    // dview mode or no server sessions - fetch from current server only
    debugLog('[ViewActions] Fetching views from current server...');

    const reqBody = {
      jsonrpc: "2.0",
      method: "config.user.getSets",
      params: [sSess, bUserID, fEview],
      id: 300
    };

    try {
      const response = await axios.post(getApiUrl(), reqBody);
      debugLog('[ViewActions] getViews response:', response.data);

      if (response.data.error) {
        console.error('[ViewActions] Backend error:', response.data.error);
        throw new Error(response.data.error.message || 'Backend returned an error');
      }

      let result = response.data.result;
      if (!result) {
        throw new Error('No result returned from backend');
      }

      // Handle [success, data] tuple format
      if (Array.isArray(result) && result.length === 2 && (typeof result[0] === 'boolean' || result[0] === 0 || result[0] === 1)) {
        if (!result[0]) {
          throw new Error('Backend operation failed');
        }
        result = result[1];
      }

      if (Array.isArray(result)) {
        // Tag slots with local server info
        allViewSlots = result.map(slot => ({
          ...slot,
          sourceSerial: localSerial,
          sourceName: state.server.sName || 'Local NVR'
        }));
      }
    } catch (e) {
      console.error('[ViewActions] Error fetching views:', e);
      throw e;
    }
  }

  // ─── eView: RAW controller views ONLY ──────────────────────────────────────────────────────
  // Show EXACTLY what the controller's config.user.getSets returns — NO combining, NO client-SYNTHESIZED
  // Smart views, NO content-strip, NO per-NVR/per-site auto-gen. The server's own flags survive and drive
  // the categories: fDefault → Default, bUser===0 → Global (else User), and fSmart===1 → Smart. Smart is
  // the SERVER's flag (Ryan, added 6.2; verified BCC returns fSmart per row 2026-08-15), NOT a client
  // aggregation like cloud's. An older backend without fSmart makes `fSmart===1` false, so those views
  // just fall into the base Default/Global/User split. categorizeViews (viewListModel) does the grouping.
  // dview + cloud fall through to the full organize below (cloud keeps its own computed Smart views).
  if (fEview) {
    const rawMap = {};
    const rawList = [];
    const rawSeen = new Set();
    allViewSlots.forEach(slot => {
      const name = slot.sName;
      if (!name || name.trim() === '' || name === 'None') return; // drop blanks + separators
      if (!rawSeen.has(name)) {
        rawSeen.add(name);
        rawList.push({ sName: name, bUser: slot.bUser, fGlobal: slot.bUser === 0, fDefault: slot.fDefault === 1, isSmart: slot.fSmart === 1, isSeparator: false });
      }
      (rawMap[name] = rawMap[name] || []).push({
        ixSlot: slot.ixSlot, bSerial: slot.bSerial, bCamera: slot.bCamera, sFisheye: slot.sFisheye || ''
      });
    });

    dispatch({ type: SET_VIEWS, payload: rawList });
    Object.keys(rawMap).forEach(name => dispatch({ type: SET_VIEW_SLOTS, viewName: name, payload: rawMap[name] }));
    debugLog(`[ViewActions] eView RAW: ${rawList.length} controller views (no combining / smart / auto-gen)`);

    // Clear a persisted current view that no longer exists in the raw set.
    const cv = state.view.currentView;
    if (cv && !cv.startsWith('__synthetic_') && !rawList.some(v => v.sName === cv)) {
      dispatch({ type: SET_CURRENT_VIEW, payload: '' });
    }
    return;
  }

  debugLog('[ViewActions] Processing', allViewSlots.length, 'total view slots');
  debugLog('[ViewActions] Processing views - fEview:', fEview, 'localSerial:', localSerial);

  // Parse the result into view list and view slots
  const viewMap = {}; // { "View Name": [slots] }
  const viewList = []; // [{ sName: "View Name", fGlobal: false, fDefault: false }]
  const seenViews = new Set();

  // eView: fold each SITE's per-NVR views into ONE. Map every member-NVR name to the view name it
  // belongs under — the site BASE when ≥2 NVRs share it ("Rosa's Cafe #02-1" & "#02-2" → "Rosa's Cafe
  // #02"), else the NVR's own full name (single-NVR sites are NOT collapsed). This drives both the
  // slot rename below AND the computed-default auto-gen further down. #86bb7phhh David.
  const eviewViewNameFor = {};
  if (isEviewLike) {
    const memberNames = (state.server.authServers && state.server.authServers.length)
      ? state.server.authServers.map(s => s.sName)
      : Array.from(new Set((state.server.allCameras || []).map(c => c.sServerName)));
    const bySite = {};
    memberNames.filter(Boolean).forEach(nm => {
      const base = systemBaseName(nm);
      (bySite[base] = bySite[base] || []).push(nm);
    });
    Object.keys(bySite).forEach(base => {
      const members = bySite[base];
      members.forEach(nm => { eviewViewNameFor[nm] = members.length > 1 ? base : nm; });
    });
  }

  allViewSlots.forEach(slot => {
    let viewName = slot.sName;

    // Skip slots without valid names
    if (!viewName || viewName.trim() === '') {
      debugWarn('[ViewActions] Skipping slot with missing/empty sName:', slot);
      return;
    }

    // "None" entries are separators - keep them but don't add to viewMap
    if (viewName === "None") {
      // Add separator to view list (always add, don't check seenViews)
      viewList.push({
        sName: "None",
        fGlobal: false,
        fDefault: false,
        isSeparator: true
      });
      return;
    }

    // In dview mode (fEview=false), filter out slots from other NVRs
    // Match Java behavior: only show views with cameras from local NVR.
    // CLOUD is exempt: its serials are per-device (bID+9999), never the localSerial, so
    // this filter would wipe every cloud slot — cloud views span devices by design.
    if (!fEview && !isCloud) {
      const slotSerial = slot.bSerial || 0;
      // Skip slots from other NVRs (bSerial != localSerial and != 0)
      // Allow bSerial=0 for backward compatibility with older views
      if (slotSerial !== 0 && slotSerial !== localSerial) {
        debugLog(`[ViewActions] dview mode: Filtering out slot from NVR ${slotSerial} (local: ${localSerial})`);
        return;
      }
    }

    // eView: fold a per-NVR PEER view into its site's ONE view. A stored view named after a member NVR
    // ("Rosa's Cafe #02-1") is renamed to the site base ("Rosa's Cafe #02") so BOTH NVRs' slots merge
    // into a single view. User views + single-NVR sites are not in the map, so they pass through
    // untouched. Also collapse the per-source viewKey to the site so the two NVRs' slots share one key.
    if (isEviewLike && eviewViewNameFor[viewName]) viewName = eviewViewNameFor[viewName];

    // Add to view list if not seen
    // In eView mode, track source server for views with same name from different servers
    const viewKey = (fEview && !eviewViewNameFor[slot.sName])
      ? `${viewName}_${slot.sourceSerial || 0}`
      : viewName;
    if (!seenViews.has(viewKey)) {
      seenViews.add(viewKey);
      viewList.push({
        sName: viewName,
        bUser: slot.bUser,
        fGlobal: slot.bUser === 0,
        fDefault: slot.fDefault === 1,
        // Carry the Dividia-internal flag onto the view-list item (cloud only ever sets it),
        // so the ViewDropdown unsaved-changes "Save" and the edit-modal pre-fill preserve it.
        // Without this it is undefined → a re-save silently flips a staff-only view to
        // customer-facing (#6, unified-viewer-cloud-views.md §12). Local rows have no
        // fDividia, so this is false there — inert for LOCAL/eview.
        fDividia: slot.fDividia === 1,
        // STORED smart view, flagged by the server (rda-backend _freezeSet emits fSmart=1
        // for any row carrying a match pattern). Distinct from the client-computed
        // aggregation below, which sets isSmart itself. Without this a stored smart view
        // arrives with isSmart undefined, and saveView's ADR-093 guard never fires — the
        // in-place save then runs delSet BEFORE addSet, so by the time the backend guard
        // looks for a stored pattern row it is already deleted and the view is silently
        // flattened into an ordinary one. An older backend omits the key → false.
        isSmart: slot.fSmart === 1,
        isSeparator: false,
        sourceSerial: slot.sourceSerial,
        sourceName: slot.sourceName
      });
    }

    // Add slot to view map - in eView, use viewKey to handle same-named views from different servers
    const mapKey = fEview ? viewKey : viewName;
    if (!viewMap[mapKey]) {
      viewMap[mapKey] = [];
    }

    // A folded multi-NVR site view merges two NVRs' slots under one key — both NVRs number their slots
    // from 0, so re-index sequentially (append) to avoid two cameras landing on the same grid position.
    // Non-folded views keep their stored ixSlot (preserves user-arranged layouts). #86bb7phhh David.
    const folded = isEviewLike
      && eviewViewNameFor[slot.sName]
      && eviewViewNameFor[slot.sName] !== slot.sName;
    viewMap[mapKey].push({
      ixSlot: folded ? viewMap[mapKey].length : slot.ixSlot,
      bSerial: slot.bSerial,
      bCamera: slot.bCamera,
      sFisheye: slot.sFisheye || ''
    });
  });

  // In dview mode, remove views that have no slots after filtering
  // (These are views that only contained cameras from other NVRs)
  if (!fEview) {
    const viewsToRemove = [];
    viewList.forEach((view, index) => {
      if (!view.isSeparator && (!viewMap[view.sName] || viewMap[view.sName].length === 0)) {
        debugLog(`[ViewActions] dview mode: Removing view "${view.sName}" (no local cameras)`);
        viewsToRemove.push(index);
        delete viewMap[view.sName];
      }
    });
    // Remove in reverse order to maintain indices
    viewsToRemove.reverse().forEach(index => {
      viewList.splice(index, 1);
    });
  }

  // Check for default views and create them if needed
  if (isEviewLike) {
    // eView / cloud mode - create a computed default view per "server" (cloud: per device)
    const authServers = state.server.authServers || [];
    // In eView mode, use allCameras (aggregated from all servers) instead of cameras
    const allCameras = state.server.allCameras || state.server.cameras || [];

    // Build server list from allCameras (more reliable than authServers for timing)
    // This ensures we have server info even if authServers hasn't been populated yet
    const serversFromCameras = {};
    allCameras.forEach(cam => {
      if (cam.bSerial && !serversFromCameras[cam.bSerial]) {
        serversFromCameras[cam.bSerial] = {
          bSerial: cam.bSerial,
          sName: cam.sServerName || `NVR ${cam.bSerial}`
        };
      }
    });

    // Merge with authServers (authServers has better names)
    const servers = authServers.length > 0 ? authServers : Object.values(serversFromCameras);

    debugLog('[ViewActions] Auto-generate check - fEview:', fEview);
    debugLog('[ViewActions] Available authServers:', authServers.length);
    debugLog('[ViewActions] Servers from cameras:', Object.keys(serversFromCameras).length);
    debugLog('[ViewActions] Using servers:', servers.map(s => `${s.sName}(${s.bSerial})`));
    debugLog('[ViewActions] Total allCameras:', allCameras.length);

    // ONE computed default view per SITE, not per NVR. Group the member NVRs by their site view name
    // (multi-NVR sites collapse to the base — "Rosa's Cafe #02-1" & "#02-2" → "Rosa's Cafe #02"; single-NVR
    // sites keep their full name — see eviewViewNameFor), then create a single default view holding EVERY
    // member NVR's cameras. This replaces the old per-NVR "#02-1"/"#02-2" default views. #86bb7phhh David.
    const sitesByName = {}; // site view name -> [server, ...]
    servers.forEach(server => {
      const siteName = eviewViewNameFor[server.sName] || systemBaseName(server.sName) || server.sName;
      (sitesByName[siteName] = sitesByName[siteName] || []).push(server);
    });

    Object.keys(sitesByName).forEach(siteName => {
      const siteServers = sitesByName[siteName];
      const siteSerials = new Set(siteServers.map(s => s.bSerial));
      const siteCameras = allCameras
        .filter(c => siteSerials.has(c.bSerial) && c.fEnable)
        .sort((a, b) => (a.bSerial !== b.bSerial ? a.bSerial - b.bSerial : a.bID - b.bID));

      debugLog('[ViewActions] Site:', siteName, '- NVRs:', [...siteSerials], '- enabled cams:', siteCameras.length);

      // Is there already a default view for this site? (named after it, OR already holding any of its cams —
      // e.g. a folded peer view from the slot loop above, or a user's own view of the site).
      const hasDefaultView = viewList.some(v => {
        if (v.isSeparator || !v.fDefault) return false;
        if (v.sName === siteName) return true;
        if (viewMap[v.sName]) return viewMap[v.sName].some(slot => siteSerials.has(slot.bSerial));
        return false;
      });
      if (hasDefaultView) {
        debugLog('[ViewActions]   - Site already has a default view, skipping');
        return;
      }

      // A view with this exact name already exists (folded peer / user view) but isn't flagged default:
      // promote it in place rather than creating a duplicate.
      const existingView = viewList.find(v => !v.isSeparator && v.sName === siteName);
      if (existingView) {
        debugLog('[ViewActions]   - Promoting existing view to default:', siteName);
        existingView.fDefault = true;
        existingView.isGenerated = true;
        return;
      }

      debugLog('[ViewActions]   - CREATING combined default view for site:', siteName);
      viewList.push({
        sName: siteName,
        fGlobal: true,
        fDefault: true,
        isSeparator: false,
        isGenerated: true  // Mark as auto-generated
      });
      viewMap[siteName] = siteCameras.map((cam, index) => ({
        ixSlot: index,
        bSerial: cam.bSerial,
        bCamera: cam.bID,
        sFisheye: ''
      }));
    });
  } else if (!isCloud) {
    // dview mode - create default view for local NVR if none exists.
    // CLOUD skips auto-default-gen here (this synthesizes a single "Local NVR" view from
    // the on-box cameras, which cloud has none of); per-device cloud defaults are Phase 4.
    const allCameras = state.server.cameras || [];
    const localNVRName = state.server.sName || 'Local NVR';

    debugLog('[ViewActions] Auto-generate check - dview mode');
    debugLog('[ViewActions] Local NVR:', localNVRName, `(${localSerial})`);
    debugLog('[ViewActions] Total cameras:', allCameras.length);

    // Check if enabled cameras exist
    const enabledCameras = allCameras.filter(c => c.fEnable);
    debugLog('[ViewActions]   - Found', enabledCameras.length, 'enabled cameras');

    // Check if there's a default view
    const hasDefaultView = viewList.some(v => !v.isSeparator && v.fDefault);
    debugLog('[ViewActions]   - Has default view?', hasDefaultView);

    if (!hasDefaultView && enabledCameras.length > 0) {
      const defaultViewName = localNVRName;

      // Check if a view with this name already exists (but isn't marked as default)
      const existingView = viewList.find(v => !v.isSeparator && v.sName === defaultViewName);

      if (existingView) {
        debugLog('[ViewActions]   - UPDATING existing view to be default:', defaultViewName);
        // Update existing view to be default
        existingView.fDefault = true;
        existingView.isGenerated = true;
      } else {
        debugLog('[ViewActions]   - CREATING default view for local NVR:', localNVRName);

        // Add new default view to view list
        viewList.push({
          sName: defaultViewName,
          fGlobal: true,
          fDefault: true,
          isSeparator: false,
          isGenerated: true  // Mark as auto-generated
        });

        // Create view slots with all enabled cameras in sequential order
        viewMap[defaultViewName] = enabledCameras.map((cam, index) => ({
          ixSlot: index,
          bSerial: localSerial || 0,
          bCamera: cam.bID,
          sFisheye: ''
        }));
      }
    }
  }

  // Generate Smart Views (eView/cloud): a camera NAME shared across ≥2 NVRs, matched CASE-INSENSITIVELY.
  // Java compares case-sensitively (so "DINING 1" and "dining 1" split) and includes junk; lite folds by
  // lowercase and excludes the on-NVR default "cam N". The set of shared names ALSO strips the controller's
  // own smart views out of the User list — it returns them tagged with the requesting user's id, so they
  // masquerade as this user's saved views (duplicates). Mirrors Java's intent, done right. #86bb7phhh David.
  const smartViews = [];
  const smartMatchSet = new Set(); // cross-NVR camera names (lowercased, incl. generic "cam N")
  if (isEviewLike) {
    const allCamerasForSmart = state.server.allCameras || [];
    const uniqueSerials = [...new Set(allCamerasForSmart.map(c => c.bSerial).filter(Boolean))];
    debugLog('[ViewActions] Smart Views check - allCameras:', allCamerasForSmart.length, 'from', uniqueSerials.length, 'servers');
    if (allCamerasForSmart.length === 0) debugWarn('[ViewActions] Smart Views: state.server.allCameras EMPTY (fetchAllServerCameras may not have completed before getViews)');

    if (allCamerasForSmart.length > 0 && uniqueSerials.length > 1) {
      const DEFAULT_CAM = /^cam\s*\d+$/; // the on-NVR default "cam N" — meaningless to aggregate across stores
      // Group ENABLED cameras by name, LOWERCASED. Blank names skipped. Numbered variants ("walk in 2" vs
      // "walk in") stay distinct keys, so they remain separate smart views (David — they're different cams).
      const byName = {}; // lowercased name -> { display, cams:[], serials:Set }
      allCamerasForSmart.forEach(cam => {
        if (!cam.fEnable) return;
        const trimmed = (cam.sName || '').trim();
        const key = trimmed.toLowerCase();
        if (!key) return;
        if (!byName[key]) byName[key] = { display: titleCase(trimmed), cams: [], serials: new Set() };
        byName[key].cams.push(cam);
        byName[key].serials.add(cam.bSerial);
      });
      Object.keys(byName).forEach(key => { if (byName[key].serials.size > 1) smartMatchSet.add(key); });

      // Drop the controller's OWN smart views out of the User category. It returns them tagged with the
      // requesting user's id AND names them by CHAIN ("O Bar", "TV Dining 1"), while the cameras are just
      // "Bar"/"Dining 1" — so a name match can't catch them. But a smart view is a SAME-NAME AGGREGATION:
      // every one of its cameras shares one camera name, and that name is cross-NVR. Real user views are
      // hand-picked (mixed camera names) → they survive. (David #86bb7phhh — why lite rolls its own.)
      const camNameByKey = {}; // "serial_bID" -> lowercased camera name
      allCamerasForSmart.forEach(c => { camNameByKey[`${c.bSerial}_${c.bID}`] = (c.sName || '').trim().toLowerCase(); });
      for (let i = viewList.length - 1; i >= 0; i--) {
        const v = viewList[i];
        if (v.isSeparator || v.isSmart || v.fDefault || v.fGlobal) continue; // only USER-category views
        const slots = viewMap[fEview ? `${v.sName}_${v.sourceSerial || 0}` : v.sName] || viewMap[v.sName] || [];
        if (!slots.length) continue;
        const names = new Set(slots.map(s => camNameByKey[`${s.bSerial}_${s.bCamera}`]).filter(Boolean));
        if (names.size === 1 && smartMatchSet.has([...names][0])) { // one shared cross-NVR name → an aggregation
          delete viewMap[fEview ? `${v.sName}_${v.sourceSerial || 0}` : v.sName];
          delete viewMap[v.sName];
          viewList.splice(i, 1);
        }
      }

      // Create one Smart view per shared name — EXCEPT the generic "cam N" (David: cam 5 across stores is
      // meaningless). Skip a name a Default/Global view already owns.
      smartMatchSet.forEach(key => {
        if (DEFAULT_CAM.test(key)) return;
        if (viewList.some(v => (v.sName || '').toLowerCase() === key)) return;
        const grp = byName[key];
        const sorted = grp.cams.sort((a, b) => (a.bSerial !== b.bSerial ? a.bSerial - b.bSerial : a.bID - b.bID));
        smartViews.push({ sName: grp.display, fGlobal: true, fDefault: false, isSeparator: false, isSmart: true });
        viewMap[grp.display] = sorted.map((cam, index) => ({ ixSlot: index, bSerial: cam.bSerial, bCamera: cam.bID, sFisheye: '' }));
      });
      debugLog('[ViewActions] Generated', smartViews.length, 'Smart Views;', smartMatchSet.size, 'cross-NVR names');
    }
  }

  // Organize views into groups with separators
  // Remove any existing "None" separators from backend (we'll add our own)
  const viewsWithoutNone = viewList.filter(v => !v.isSeparator);

  // Categorize views - prevent duplicates between Default and Global
  // Default views: fDefault=true
  // Global views: bUser=0, fDefault=false (exclude smart views - they go in their own section)
  // User views: bUser!=0
  const defaultViews = viewsWithoutNone.filter(v => v.fDefault);
  const globalViews = viewsWithoutNone.filter(v => v.fGlobal && !v.fDefault && !v.isSmart);
  // !isSmart here too, not just on globalViews: a PERSONAL smart view (bUser != 0) would
  // otherwise land under User. Both exclusions matter now that isSmart can come from the
  // SERVER's fSmart flag and not only from the client-side aggregation below — before that,
  // no stored view ever carried isSmart, so excluding it from a bucket could not drop one.
  const userViews = viewsWithoutNone.filter(v => !v.fGlobal && !v.fDefault && !v.isSmart);
  // STORED smart views, flagged by the backend. They are excluded from Global AND User
  // above, so without re-adding them here they would vanish from the list entirely.
  const storedSmartViews = viewsWithoutNone.filter(v => v.isSmart);

  const organizedViews = [];

  // Add default views (DVR-named views)
  if (defaultViews.length > 0) {
    organizedViews.push(...defaultViews);
  }

  // Add separator before global views if we have both
  if (globalViews.length > 0 && defaultViews.length > 0) {
    organizedViews.push({
      sName: "None",
      fGlobal: false,
      fDefault: false,
      isSeparator: true
    });
  }

  // Add global views
  if (globalViews.length > 0) {
    organizedViews.push(...globalViews);
  }

  // Smart section = the backend's stored rule views PLUS lite's own cross-NVR name
  // aggregation. The client synthesis already skips any name already in viewList, so the
  // two cannot collide.
  const allSmartViews = storedSmartViews.concat(smartViews);

  // Add separator before smart views
  if (allSmartViews.length > 0 && (defaultViews.length > 0 || globalViews.length > 0)) {
    organizedViews.push({
      sName: "None",
      fGlobal: false,
      fDefault: false,
      isSeparator: true
    });
  }

  // Add smart views (sorted alphabetically)
  if (allSmartViews.length > 0) {
    const sortedSmartViews = allSmartViews.sort((a, b) => a.sName.localeCompare(b.sName));
    organizedViews.push(...sortedSmartViews);
  }

  // Add separator before user views if we have any previous groups
  if (userViews.length > 0 && (defaultViews.length > 0 || globalViews.length > 0 || smartViews.length > 0)) {
    organizedViews.push({
      sName: "None",
      fGlobal: false,
      fDefault: false,
      isSeparator: true
    });
  }

  // Add user views
  if (userViews.length > 0) {
    organizedViews.push(...userViews);
  }

  // Dispatch organized view list
  dispatch({ type: SET_VIEWS, payload: organizedViews });

  // Dispatch all view slots (including auto-generated and smart views)
  Object.keys(viewMap).forEach(viewName => {
    dispatch({
      type: SET_VIEW_SLOTS,
      viewName: viewName,
      payload: viewMap[viewName]
    });
  });

  debugLog('[ViewActions] Loaded views by category:');
  debugLog('  - Default:', defaultViews.map(v => v.sName));
  debugLog('  - Global:', globalViews.map(v => v.sName));
  debugLog('  - Smart:', smartViews.map(v => v.sName));
  debugLog('  - User:', userViews.map(v => v.sName));

  // Validate currentView - if it doesn't exist in the new views list, clear it
  // This handles cases where persisted state has a view that no longer exists
  // (e.g., Smart Views that weren't regenerated, or deleted views)
  const currentView = state.view.currentView;
  if (currentView && !currentView.startsWith('__synthetic_')) {
    const viewExists = organizedViews.some(v => !v.isSeparator && v.sName === currentView);
    if (!viewExists) {
      debugWarn('[ViewActions] Current view not found in loaded views, clearing:', currentView);
      dispatch({ type: SET_CURRENT_VIEW, payload: '' });
    }
  }
};

/**
 * Select a view and apply its camera layout
 *
 * CAMERA SOURCES:
 *   In dview mode: Uses local cameras from state.server.cameras
 *   In eView mode: Uses aggregated cameras from state.server.allCameras (multi-NVR)
 *
 * VIEW SLOTS KEY NAMING CONVENTION:
 *   When views are loaded in eView mode, viewSlots keys are stored with a serial suffix
 *   to distinguish views from different servers that may have the same name.
 *
 *   dview mode key format:  "ViewName"        (e.g., "QA Test View")
 *   eView mode key format:  "ViewName_serial" (e.g., "QA Test View_4002")
 *
 *   The dropdown passes just the view name (without suffix), so we must search for
 *   a matching key with the serial suffix pattern when exact match fails in eView mode.
 *
 * @param {string} sViewName - Name of view to load (without serial suffix)
 */
export const selectView = (sViewName) => (dispatch, getState) => {
  const state = getState();
  const activeServerSerial = state.server.activeServerSerial;
  const bSerial = state.server.bSerial; // Local server serial for persistence
  const currentConf = state.config.conf; // Get current conf - DON'T change it!
  const isViewActive = state.view.isViewActive;

  // In eView mode, use allCameras (aggregated from all servers)
  // In dview mode, use local cameras.
  // CLOUD rides the eView path: its views span devices, so cameras match by
  // (bSerial, bCamera) against the cross-device allCameras pool (bSerial = bID+9999).
  const isEviewMode = isMultiServerSession(state) || getBackend(state).kind === 'cloud';
  const camerasSource = isEviewMode ? state.server.allCameras : state.server.cameras;

  debugLog('[ViewActions] selectView called for:', sViewName);
  debugLog('[ViewActions] Mode:', isEviewMode ? 'eView (multi-NVR)' : 'dview (single-NVR)');
  debugLog('[ViewActions] Available viewSlots keys:', Object.keys(state.view.viewSlots));

  // Find view slots - in eView mode, keys may have _serial suffix
  // Try exact match first (works for dview mode and some eView cases)
  let viewSlots = state.view.viewSlots[sViewName];

  // eView mode key lookup with serial suffix pattern matching
  // The view dropdown passes just the view name (e.g., "QA Test View") but viewSlots
  // stores keys with serial suffix (e.g., "QA Test View_4002") to distinguish same-named
  // views from different servers. This pattern matching finds the correct key.
  if (!viewSlots && isEviewMode) {
    const viewSlotsKeys = Object.keys(state.view.viewSlots);
    // Build regex pattern: escape special chars in view name, match underscore + digits at end
    // Example: "QA Test View" matches "QA Test View_4002" but not "QA Test View Extra_4002"
    const matchingKey = viewSlotsKeys.find(key => {
      const pattern = new RegExp(`^${sViewName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_\\d+$`);
      return pattern.test(key);
    });
    if (matchingKey) {
      if (process.env.NODE_ENV === 'development') {
        debugLog('[ViewActions] Found matching key with serial suffix:', matchingKey);
      }
      viewSlots = state.view.viewSlots[matchingKey];
    }
  }

  debugLog('[ViewActions] viewSlots for this view:', viewSlots);

  if (!viewSlots) {
    debugWarn('[ViewActions] View not found:', sViewName);
    return;
  }

  if (!camerasSource || camerasSource.length === 0) {
    debugWarn('[ViewActions] No cameras available');
    debugWarn('[ViewActions] isEviewMode:', isEviewMode, 'allCameras:', state.server.allCameras?.length, 'cameras:', state.server.cameras?.length);
    return;
  }

  debugLog('[ViewActions] Applying view:', sViewName, 'with', viewSlots.length, 'slots');
  debugLog('[ViewActions] Camera source has', camerasSource.length, 'cameras');

  // Save last selected view to localStorage (mirrors Java CookieModel)
  // Use activeServerSerial in eview mode, local bSerial in dview mode. Cloud has no NVR
  // serial (its per-device serials are synthetic), so it uses a stable 'cloud' key.
  const isCloud = getBackend(state).kind === 'cloud';
  const serverSerial = isCloud ? 'cloud' : (isEviewMode ? (activeServerSerial || bSerial) : bSerial);
  saveLastSelectedView(serverSerial, sViewName);
  debugLog('[ViewActions] Current conf:', currentConf, 'isEviewMode:', isEviewMode);

  // Save original cameras before first view is applied
  if (!isViewActive) {
    debugLog('[ViewActions] Saving original cameras array');
    dispatch({ type: SAVE_ORIGINAL_CAMERAS, payload: camerasSource });
  }

  // Update current view name
  dispatch({ type: SET_CURRENT_VIEW, payload: sViewName });

  // Create a full 64-camera array initialized with disabled placeholders
  const reorderedCameras = [];
  for (let i = 0; i < 64; i++) {
    reorderedCameras.push({
      bID: i + 1,
      bSerial: isEviewMode ? (activeServerSerial || bSerial) : bSerial,
      sName: `Empty Slot ${i + 1}`,
      fEnable: false,
      compositeKey: `empty_${i + 1}`
    });
  }

  // Process all slots from the view
  let slotsToProcess = viewSlots;
  debugLog(`[ViewActions] Processing ${viewSlots.length} slots`);

  // Fill in the view's camera slots with actual cameras
  // In eView mode: ALWAYS use allCameras for lookup (contains cameras from all NVRs)
  // In dview mode: use originalCameras if view is active, otherwise use cameras
  // The originalCameras is for restoring layout, but eView needs full allCameras for bSerial+bID lookup
  const lookupSource = isEviewMode
    ? (state.server.allCameras || camerasSource)  // eView: always use full allCameras
    : (isViewActive ? state.view.originalCameras : camerasSource);  // dview: restore behavior
  debugLog('[ViewActions] Processing', slotsToProcess.length, 'slots, lookup source has', lookupSource?.length, 'cameras');
  debugLog('[ViewActions] allCameras available:', state.server.allCameras?.length, 'cameras:', state.server.cameras?.length);

  slotsToProcess.forEach(slot => {
    const slotIndex = slot.ixSlot;
    if (slotIndex >= 0 && slotIndex < 64) {
      // Find matching camera from the camera source
      // In eView mode: match both bID AND bSerial (cameras from multiple servers)
      // In dview mode: match only bID (single server)
      const matchedCamera = lookupSource?.find(c => {
        if (isEviewMode) {
          // eView: match both bID and bSerial to find camera from correct server
          return c.bID === slot.bCamera && c.bSerial === slot.bSerial;
        } else {
          // dview: just match bID (single NVR)
          return c.bID === slot.bCamera;
        }
      });

      if (matchedCamera) {
        // Use the actual camera object with a unique key for this slot
        // This prevents duplicate key warnings when same camera appears in multiple slots
        reorderedCameras[slotIndex] = {
          ...matchedCamera,
          compositeKey: `slot_${slotIndex}_${matchedCamera.compositeKey || matchedCamera.bID}`
        };
        debugLog(`[ViewActions] ✓ Slot ${slotIndex}: Camera ${matchedCamera.bID} "${matchedCamera.sName}" (Serial ${matchedCamera.bSerial})`);
      } else {
        // Camera not found - add a placeholder with unique key
        // This can happen if camera is offline or user doesn't have access
        debugWarn(`[ViewActions] Slot ${slotIndex}: Camera ${slot.bCamera} (Serial ${slot.bSerial}) not found`);
        reorderedCameras[slotIndex] = {
          bID: slot.bCamera,
          bSerial: slot.bSerial || bSerial,
          sName: `Camera ${slot.bCamera} (Not Found)`,
          fEnable: false,
          compositeKey: isEviewMode ? `notfound_${slot.bSerial}_${slot.bCamera}_${slotIndex}` : `notfound_${slot.bCamera}_${slotIndex}`
        };
      }
    }
  });

  // Determine currentCamView (the SET/bank) from the conf's first bank — but PRESERVE the current set when
  // RE-selecting the SAME view (a Live<->Playback re-mount re-runs autoSelectDefaultView, which would
  // otherwise snap the shared set back to bank 1). Only a genuine view CHANGE resets to the first bank —
  // a new view starts at set 1. Keeps view + conf + set consistent across the two viewers (#86bb7phhh).
  const reselectingSameView = state.view && state.view.currentView === sViewName;
  const existingCamView = state.camera && state.camera.currentCamView;
  let currentCamView;
  switch (currentConf) {
    case 'conf-1':
      // Single camera view - show first camera slot
      currentCamView = 'cam_1';
      break;
    case 'conf-2':
      currentCamView = 'cam_1-2';
      break;
    case 'conf-4':
      currentCamView = 'cam_1-4';
      break;
    case 'conf-6':
      currentCamView = 'cam_1-6';
      break;
    case 'conf-8':
      currentCamView = 'cam_1-8';
      break;
    case 'conf-9':
      currentCamView = 'cam_1-9';
      break;
    case 'conf-12':
      currentCamView = 'cam_1-12';
      break;
    case 'conf-16':
      currentCamView = 'cam_1-16';
      break;
    default:
      currentCamView = 'cam_1-4';
  }
  // Re-selecting the current view (re-mount / nav between Live and Playback) keeps the shared set.
  if (reselectingSameView && existingCamView) currentCamView = existingCamView;

  debugLog('[ViewActions] Full camera array length:', reorderedCameras.length);
  const enabledCount = reorderedCameras.filter(c => c.fEnable).length;
  debugLog('[ViewActions] Enabled cameras in view:', enabledCount);
  debugLog('[ViewActions] Setting currentCamView to:', currentCamView, '(keeping conf:', currentConf + ')');

  // Dispatch actions to apply the view layout
  dispatch({ type: SET_ALL_CAMERAS, payload: reorderedCameras });
  // Snapshot this freshly-applied layout as the pristine baseline, so the "unsaved" dot
  // compares against it and clears when a live edit is reverted (#86bb5dx62).
  dispatch({ type: SET_VIEW_PRISTINE, payload: reorderedCameras.map(c => ({ ...c })) });
  // DON'T dispatch CONFIG_CHANGED - keep the user's manually selected layout!
  dispatch({ type: CAM_VIEW_CHANGED, payload: currentCamView });
  dispatch({ type: CLEAR_VIEW_DIRTY });

  debugLog('[ViewActions] Applied view successfully');
};

/**
 * Clear view selection and restore original cameras
 */
export const clearView = () => (dispatch, getState) => {
  const state = getState();
  const originalCameras = state.view.originalCameras;

  if (originalCameras) {
    debugLog('[ViewActions] Restoring original cameras');
    dispatch({ type: SET_ALL_CAMERAS, payload: originalCameras });
  }

  dispatch({ type: RESTORE_ORIGINAL_CAMERAS });
};

/**
 * Auto-select default view if available
 *
 * Priority (mirrors Java DView.userLoggedIn()):
 * 1. User's last selected view (from localStorage, like Java's CookieModel)
 * 2. System default view (fDefault=true)
 * 3. First available view
 * 4. Synthesize view from all cameras (if no views exist)
 */
export const autoSelectDefaultView = () => (dispatch, getState) => {
  const state = getState();
  const views = state.view.views;
  const viewSlots = state.view.viewSlots;
  const dataLoaded = state.server.dataLoaded;
  const isEviewMode = isMultiServerSession(state);
  const activeServerSerial = state.server.activeServerSerial;
  const bSerial = state.server.bSerial; // Local/controller server serial

  // CLOUD: no NVR serial and no local `dataLoaded` flag, so the shared guards below don't
  // apply — select the grid default directly (unified-viewer-cloud-views.md §7): the user's
  // remembered last view, else the resolved default (a user fDefault view, or the first
  // computed per-device default), else the first available view. selectView rebuilds the
  // grid from the all-devices pool. Used on login (grid defaults to a device view, not the
  // raw pool) and after a delete (reselect a default).
  const isCloud = getBackend(state).kind === 'cloud';
  if (isCloud) {
    const available = (views || []).filter(v => !v.isSeparator);
    if (!available.length) { debugLog('[ViewActions] cloud auto-select: no views to select'); return; }
    const saved = loadLastSelectedView('cloud');
    const selected = (saved && available.find(v => v.sName === saved))
      || available.find(v => v.fDefault)
      || available[0];
    if (selected) { debugLog('[ViewActions] cloud auto-select:', selected.sName); dispatch(selectView(selected.sName)); }
    return;
  }

  // Only proceed if data is loaded and views are available
  if (!dataLoaded || !views || views.length === 0) {
    debugLog('[ViewActions] Not ready for auto-select: dataLoaded=', dataLoaded, 'views=', views?.length);
    return;
  }

  // In eView mode: use bSerial (controller) for initial view, or activeServerSerial if switching servers
  // In dview mode: use bSerial (local server)
  const serverSerial = isEviewMode ? (activeServerSerial || bSerial) : bSerial;

  // Guard: Wait for bSerial to be set (should be set by getServerConf during login)
  if (!serverSerial || serverSerial === 0) {
    debugLog('[ViewActions] Server serial not yet available, deferring auto-select');
    return;
  }

  debugLog('[ViewActions] autoSelectDefaultView - serverSerial:', serverSerial, 'isEviewMode:', isEviewMode, 'bSerial:', bSerial);

  let selectedView = null;

  if (isEviewMode) {
    // In eView mode, find view for the controller or active server
    const targetSerial = activeServerSerial || bSerial;
    debugLog('[ViewActions] eView mode - looking for view for server:', targetSerial);

    // A view "has cameras" when it holds at least one slot (David: never land a no-cam
    // system on its empty view). Used by the Priority-1 saved-view guard + the Priority-5
    // fallback below.
    const hasCams = (v) => { const slots = viewSlots[v.sName]; return Array.isArray(slots) && slots.length > 0; };

    // Filter views to those with cameras from the target server
    const serverViews = views.filter(v => {
      if (v.isSeparator) return false;
      const slots = viewSlots[v.sName];
      if (!slots) return false;
      // Check if this view has any cameras from the target server
      return slots.some(slot => slot.bSerial === targetSerial);
    });

    debugLog('[ViewActions] Views with cameras from server', targetSerial + ':', serverViews.map(v => v.sName));

    // Priority 1: Check localStorage for last selected view (like Java's CookieModel)
    // Look across ALL non-separator views, not just controller-local ones.
    // Eview users routinely save cross-NVR views (e.g. Rosa's Cafe #01 while
    // signed into BCC Controller); restricting the lookup to serverViews
    // means any non-controller-local saved view is treated as "no longer
    // exists" and gets purged on the next login.
    const savedViewName = loadLastSelectedView(serverSerial);
    if (savedViewName) {
      const savedView = views.find(v => !v.isSeparator && v.sName === savedViewName);
      if (savedView && hasCams(savedView)) {
        debugLog('[ViewActions] Found saved view in localStorage:', savedViewName);
        selectedView = savedView;
      } else if (savedView) {
        // Saved view is EMPTY (e.g. a gateway controller's own view) — don't land on it;
        // fall through to a view that actually has cameras (Priority 5).
        debugLog('[ViewActions] Saved view has no cameras, skipping:', savedViewName);
      } else {
        debugLog('[ViewActions] Saved view no longer exists:', savedViewName);
        clearLastSelectedView(serverSerial);
      }
    }

    // Priority 2: Find default view among server-specific views
    if (!selectedView) {
      selectedView = serverViews.find(v => v.fDefault);
      if (selectedView) {
        debugLog('[ViewActions] Using default view:', selectedView.sName);
      }
    }

    // Priority 3: Use first view for this server
    if (!selectedView && serverViews.length > 0) {
      selectedView = serverViews[0];
      debugLog('[ViewActions] No default found, using first view for server:', selectedView.sName);
    }

    // Priority 4: Synthesize view if no views exist for this server
    if (!selectedView) {
      debugLog('[ViewActions] No views found for server - synthesizing default view');
      // In eView mode, use allCameras which contains cameras from all connected servers
      const allCameras = state.server.allCameras || state.server.cameras || [];
      const serverCameras = allCameras.filter(c => c.bSerial === targetSerial && c.fEnable);

      if (serverCameras.length > 0) {
        debugLog('[ViewActions] Found', serverCameras.length, 'cameras for server, creating synthetic view');

        // Create synthetic view slots
        const syntheticSlots = serverCameras.map((cam, index) => ({
          ixSlot: index,
          bSerial: cam.bSerial,
          bCamera: cam.bID,
          sFisheye: ''
        }));

        // Store synthetic view
        const syntheticViewName = `__synthetic_${targetSerial}`;
        dispatch({
          type: SET_VIEW_SLOTS,
          viewName: syntheticViewName,
          payload: syntheticSlots
        });

        // Create synthetic view object
        selectedView = { sName: syntheticViewName, fGlobal: false, fDefault: false };
      }
    }

    // Priority 5: Fall through to a view across all servers. Hit on eView CONTROLLERS
    // that have zero local cameras (gateway-only boxes like BCC Controller). David: don't
    // land on the controller's own EMPTY view — prefer the first view that actually HAS
    // cameras (so a no-cam system shows real video), only falling back to any view if none do.
    if (!selectedView) {
      const withCams = views.find(v => !v.isSeparator && hasCams(v));
      selectedView = withCams || views.find(v => !v.isSeparator);
      if (selectedView) {
        debugLog('[ViewActions] Controller has no local cams — using first view with cameras:', selectedView.sName, withCams ? '(has cams)' : '(fallback, empty)');
      }
    }
  } else {
    // Single-NVR mode (dview) or no active server
    debugLog('[ViewActions] dview mode - looking for view for local server:', bSerial);

    // Get non-separator views
    const availableViews = views.filter(v => !v.isSeparator);

    // Priority 1: Check localStorage for last selected view (like Java's CookieModel)
    const savedViewName = loadLastSelectedView(serverSerial);
    if (savedViewName) {
      const savedView = availableViews.find(v => v.sName === savedViewName);
      if (savedView) {
        debugLog('[ViewActions] Found saved view in localStorage:', savedViewName);
        selectedView = savedView;
      } else {
        debugLog('[ViewActions] Saved view no longer exists:', savedViewName);
        clearLastSelectedView(serverSerial);
      }
    }

    // Priority 2: Find default view (fDefault=true)
    if (!selectedView) {
      selectedView = availableViews.find(v => v.fDefault);
      if (selectedView) {
        debugLog('[ViewActions] Using default view:', selectedView.sName);
      }
    }

    // Priority 3: Use first available view
    if (!selectedView && availableViews.length > 0) {
      selectedView = availableViews[0];
      debugLog('[ViewActions] No default found, using first view:', selectedView.sName);
    }
  }

  if (selectedView) {
    debugLog('[ViewActions] Auto-selecting view:', selectedView.sName);
    dispatch(selectView(selectedView.sName));
  } else {
    debugLog('[ViewActions] No view found to select');
  }
};

/**
 * Save current camera layout to a view (create or update)
 *
 * @param {string} sSess - Session token
 * @param {number} bUserID - User ID (0 for global views)
 * @param {string} sViewName - Name of the view
 * @param {boolean} fGlobal - Is this a global view (all users)?
 * @param {boolean} fDefault - Is this a default view?
 */
export const saveView = (sSess, bUserID, sViewName, fGlobal = false, fDefault = false, isUpdate = false, oldViewName = null, fDividia = false) => (dispatch, getState) => {
  const state = getState();
  const fEview = state.server.fEview; // server-capability flag — passed to backend getViews RPC
  const views = state.view.views;

  // In eView mode, use the current camera layout (which may include cameras from multiple NVRs)
  // In dview mode, use local cameras only
  const isEviewMode = isMultiServerSession(state);
  const cameras = state.server.cameras;  // Current view layout (already has cameras from all servers when in eView)

  if (!sViewName || sViewName.trim() === '') {
    console.error('[ViewActions] View name is required');
    return Promise.reject(new Error('View name is required'));
  }

  // A SMART view is a match RULE, not a stored camera list. On a local/eView NVR the backend
  // keeps ONE sMatchValue pattern row and expands it at query time; in cloud (and in lite's
  // own eView aggregation) the camera set is computed client-side. Either way what is on
  // screen is DERIVED, so saving it back over the same name replaces the rule with a frozen
  // snapshot — and because the save "succeeds", the operator is never told the view stopped
  // being smart.
  //
  // rda-backend now refuses this for a STORED smart view (ADR-093), which is the durable
  // fix. The guard is repeated here because lite's client-computed aggregations have no
  // stored row for the backend to recognize, so only the client can catch those. It also
  // gives the user a useful message instead of a raw RPC error.
  //
  // Scoped to an IN-PLACE save: arranging a smart view stays allowed, and "Save As" under a
  // NEW name is the supported way to keep the arrangement (matching the Java viewer, where
  // checkSetIsWritable returns false for a smart view but Save-As is offered).
  const oSmartTarget = (views || []).find(v =>
    v && !v.isSeparator && v.isSmart &&
    (v.sName === sViewName || (oldViewName && v.sName === oldViewName)));
  if (oSmartTarget) {
    debugWarn('[ViewActions] refusing in-place save over smart view:', oSmartTarget.sName);
    return Promise.reject(new Error(
      `"${oSmartTarget.sName}" is a smart view and cannot be saved over. ` +
      'Use Save As to keep this layout as a new view.'));
  }

  // CLOUD: persist via the CloudNvrAdapter (POST /ve/view) into the normalized schema,
  // not the JSON-RPC addSet/delSet dance. The current layout (state.server.cameras) is
  // converted to the cloud cams shape inside the adapter. The scope flags drive the
  // BACKEND role gate (unified-viewer-cloud-views.md §6): a global (fGlobal) is owned by
  // bUserID=0 (admin only); otherwise the view is owned by the caller (bUserID = the
  // logged-in user id). fDefault marks the user's default for the device(s) it covers
  // (§7); fDividia marks a Dividia-internal global (bRole=1 only). An UPDATE reuses the
  // existing view's cloud id (resolved by name — the backend keys by id, the UI works in
  // names; a rename passes the old name to find the id, then writes the new name). Then
  // refresh + select. (No addToUser — delivery is device-access-driven, not sViews.)
  const backend = getBackend(state);
  if (backend && backend.kind === 'cloud') {
    const cloudViews = backend.getViews() || [];
    const existing = cloudViews.find(v => v.label === (oldViewName || sViewName) || v.label === sViewName);
    return backend.saveView({
      id: existing ? existing.id : undefined,
      name: sViewName,
      cameras: state.server.cameras,
      bUserID: fGlobal ? 0 : bUserID,   // 0 = customer-global · else = the caller (owner)
      fDefault,
      fDividia,
    }).then((res) => {
      if (!res || !res.ok) throw new Error('Failed to save view');
      debugLog('[ViewActions] cloud view saved:', sViewName, 'id', res.id, 'global', fGlobal, 'default', fDefault, 'dividia', fDividia);
      return dispatch(getViews(sSess, bUserID, fEview)).then(() => sViewName);
    });
  }

  debugLog('[ViewActions] saveView called with:', {
    sSess,
    bUserID,
    sViewName,
    fGlobal,
    fDefault,
    isUpdate,
    oldViewName,
    isEviewMode
  });

  // Check if view already exists (either by same name or old name for renames)
  const existingView = views.find(v =>
    !v.isSeparator && (v.sName === sViewName || v.sName === oldViewName)
  );

  debugLog('[ViewActions] Existing view check:', existingView ? existingView.sName : 'none');

  // Build slot data array - preserve slot positions (like Java dview)
  // Iterate through ALL cameras, only include enabled ones, but use their actual slot index
  const rgoData = [];
  cameras.forEach((cam, slotIndex) => {
    if (cam.fEnable) {
      rgoData.push({
        sName: sViewName,
        ixSlot: slotIndex,  // Use actual position, not filtered index
        bSerial: cam.bSerial || 0,
        bCamera: cam.bID,
        sFisheye: cam.sFisheye || '',
        fDefault: fDefault ? 1 : 0
      });
    }
  });

  debugLog('[ViewActions] Found', rgoData.length, 'enabled cameras (preserving slot positions)');

  debugLog('[ViewActions] Built rgoData with', rgoData.length, 'slots (with preserved positions)');
  if (rgoData.length <= 10) {
    // In eView mode, also show bSerial to track which NVR each camera belongs to
    debugLog('[ViewActions] rgoData:', rgoData.map(s =>
      isEviewMode ? `slot ${s.ixSlot}: cam ${s.bCamera} (NVR ${s.bSerial})` : `slot ${s.ixSlot}: cam ${s.bCamera}`
    ));
  }

  // Use bUserID=0 for global views, else current user ID
  // Fallback to 0 if bUserID is undefined (user data not loaded)
  let userID = fGlobal ? 0 : bUserID;

  if (userID === undefined || userID === null) {
    debugWarn('[ViewActions] bUserID is undefined/null, defaulting to 0 (admin/global)');
    debugWarn('[ViewActions] User data may not be loaded properly during login');
    userID = 0;
  }

  debugLog('[ViewActions] Using bUser:', userID, '(fGlobal:', fGlobal, ', bUserID:', bUserID + ')');

  const reqBody = {
    jsonrpc: "2.0",
    method: "config.user.addSet",
    params: [sSess, userID, rgoData],
    id: 302
  };

  debugLog('[ViewActions] Request body:', reqBody);

  // If updating an existing view, delete it first to avoid duplicate key errors
  let deletePromise = Promise.resolve();

  if (existingView) {
    debugLog('[ViewActions] Deleting existing view before save:', existingView.sName);
    const deleteUserID = existingView.fGlobal ? 0 : bUserID;
    const deleteReqBody = {
      jsonrpc: "2.0",
      method: "config.user.delSet",
      params: [sSess, deleteUserID, existingView.sName],
      id: 303
    };
    deletePromise = axios.post(getApiUrl(), deleteReqBody)
      .then(response => {
        debugLog('[ViewActions] Existing view deleted successfully');
        return response;
      })
      .catch(error => {
        debugWarn('[ViewActions] Error deleting existing view (may not exist):', error);
        // Continue anyway - maybe view doesn't exist
        return Promise.resolve();
      });
  }

  return deletePromise.then(() => axios.post(getApiUrl(), reqBody))
    .then(response => {
      debugLog('[ViewActions] saveView response:', response.data);

      // Check for backend errors
      if (response.data.error) {
        console.error('[ViewActions] Backend error saving view:', response.data.error);
        console.error('[ViewActions] Error code:', response.data.error.code);
        console.error('[ViewActions] Error message:', response.data.error.message);
        throw new Error(response.data.error.message || 'Failed to save view');
      }

      // Check for tuple response format - backend may return boolean or numeric (1/0)
      const result = response.data.result;
      if (Array.isArray(result) && result.length === 2) {
        const firstEl = result[0];
        if (typeof firstEl === 'boolean' || firstEl === 0 || firstEl === 1) {
          debugLog('[ViewActions] Save result (tuple format):', result);
          if (!firstEl) {
            console.error('[ViewActions] Save failed - backend returned false');
            throw new Error('Backend returned failure status');
          }
        } else {
          debugLog('[ViewActions] Save result:', result);
        }
      } else {
        debugLog('[ViewActions] Save result:', result);
      }

      debugLog('[ViewActions] View saved successfully:', sViewName);
      debugLog('[ViewActions] Refreshing views with:', { sSess, bUserID, fEview });
      // Refresh views to show updated list
      return dispatch(getViews(sSess, bUserID, fEview))
        .then(() => {
          debugLog('[ViewActions] Views refreshed after save, view should be available now');
          // Return the view name so caller knows which view to select
          return sViewName;
        });
    })
    .catch(error => {
      console.error('[ViewActions] Error saving view:', error);
      console.error('[ViewActions] Error details:', error.response?.data);
      console.error('[ViewActions] Error message:', error.message);
      if (error.response) {
        console.error('[ViewActions] Response status:', error.response.status);
        console.error('[ViewActions] Response data:', error.response.data);
      }
      throw error;
    });
};

/**
 * Delete a view
 *
 * @param {string} sSess - Session token
 * @param {number} bUserID - User ID (0 for global views)
 * @param {string} sViewName - Name of the view to delete
 * @param {boolean} fGlobal - Is this a global view?
 */
export const deleteView = (sSess, bUserID, sViewName, fGlobal = false) => (dispatch, getState) => {
  const state = getState();
  const fEview = state.server.fEview;
  const bSerial = state.server.bSerial;
  const activeServerSerial = state.server.activeServerSerial;

  if (!sViewName || sViewName.trim() === '') {
    console.error('[ViewActions] View name is required for deletion');
    return Promise.reject(new Error('View name is required'));
  }

  // CLOUD: delete via the CloudNvrAdapter (POST /ve/view {del:1}). The backend keys by
  // the view's id, so resolve it from the cloud view list by name; then mirror the local
  // post-delete behavior (clear the current view if it was the deleted one) + refresh.
  const backend = getBackend(state);
  if (backend && backend.kind === 'cloud') {
    const existing = (backend.getViews() || []).find(v => v.label === sViewName);
    if (!existing) return Promise.reject(new Error('View not found'));
    return backend.deleteView(existing.id).then((res) => {
      if (!res || !res.ok) throw new Error('Failed to delete view');
      // Mirror the local branch below: if the deleted view was the saved last view, clear it
      // (cloud uses a fixed 'cloud' serial key — see selectView) so autoSelectDefaultView
      // can't try to rehydrate a now-deleted view name on the next login.
      if (loadLastSelectedView('cloud') === sViewName) clearLastSelectedView('cloud');
      if (state.view.currentView === sViewName) dispatch(clearView());
      return dispatch(getViews(sSess, bUserID, fEview));
    });
  }

  debugLog('[ViewActions] Deleting view:', sViewName, 'global:', fGlobal);

  // Use bUserID=0 for global views, else current user ID
  const userID = fGlobal ? 0 : bUserID;

  const reqBody = {
    jsonrpc: "2.0",
    method: "config.user.delSet",
    params: [sSess, userID, sViewName],
    id: 303
  };

  return axios.post(getApiUrl(), reqBody)
    .then(response => {
      debugLog('[ViewActions] deleteView response:', response.data);

      // Check for backend errors
      if (response.data.error) {
        console.error('[ViewActions] Backend error deleting view:', response.data.error);
        throw new Error(response.data.error.message || 'Failed to delete view');
      }

      debugLog('[ViewActions] View deleted successfully:', sViewName);

      // Clear localStorage if deleted view was the saved last view
      const serverSerial = fEview ? activeServerSerial : bSerial;
      const savedView = loadLastSelectedView(serverSerial);
      if (savedView === sViewName) {
        debugLog('[ViewActions] Clearing saved view from localStorage (was deleted)');
        clearLastSelectedView(serverSerial);
      }

      // Clear current view selection if it was the deleted view
      if (state.view.currentView === sViewName) {
        dispatch(clearView());
      }
      // Refresh views to show updated list
      return dispatch(getViews(sSess, bUserID, fEview));
    })
    .catch(error => {
      console.error('[ViewActions] Error deleting view:', error);
      console.error('[ViewActions] Error details:', error.response?.data);
      throw error;
    });
};

/**
 * Assign a camera to a specific slot
 *
 * @param {number} slotIndex - Slot index (0-63)
 * @param {object|null} camera - Camera object to assign (null to clear slot)
 */
export const assignCameraToSlot = (slotIndex, camera) => (dispatch, getState) => {
  const state = getState();
  const cameras = [...state.server.cameras]; // Clone array
  const fEview = state.server.fEview; // server-capability flag — used for empty-slot serial tagging
  const isEviewMode = isMultiServerSession(state);
  const activeServerSerial = state.server.activeServerSerial;

  debugLog('[ViewActions] Assigning camera to slot', slotIndex, ':', camera ? camera.sName : 'CLEAR');

  if (slotIndex < 0 || slotIndex >= cameras.length) {
    console.error('[ViewActions] Invalid slot index:', slotIndex);
    return;
  }

  if (camera) {
    // Assign camera to slot with unique composite key
    // In eView mode, preserve bSerial, sServerName, serverUrl for multi-NVR streaming
    cameras[slotIndex] = {
      ...camera,
      compositeKey: `slot_${slotIndex}_${camera.compositeKey || camera.bID}`
    };
    if (isEviewMode) {
      debugLog('[ViewActions] eView camera assigned:', {
        bID: camera.bID,
        bSerial: camera.bSerial,
        sServerName: camera.sServerName,
        serverUrl: camera.serverUrl
      });
    }
  } else {
    // Clear slot - create empty placeholder
    cameras[slotIndex] = {
      bID: slotIndex + 1,
      bSerial: fEview ? activeServerSerial : 0,
      sName: `Empty Slot ${slotIndex + 1}`,
      fEnable: false,
      compositeKey: `empty_${slotIndex + 1}`
    };
  }

  dispatch({ type: ASSIGN_CAMERA_TO_SLOT, payload: cameras });
  dispatch({ type: SET_VIEW_DIRTY, payload: true });

  debugLog('[ViewActions] View marked as dirty (unsaved changes)');
};

// Swap the cameras occupying two absolute slot positions — drag-to-reorder the cameras
// currently on view (#86bb5dx62 L-D9, mobile-portrait). Preserves each camera object
// (incl. compositeKey / stream identity) so React's keyed reconciliation MOVES the existing
// tiles instead of remounting them — the live streams follow their cameras to the new cells
// with no flash/restart. Marks the view dirty (unsaved), same as an assign.
export const swapCameraSlots = (indexA, indexB) => (dispatch, getState) => {
  if (indexA === indexB) return;
  const cameras = [...getState().server.cameras];
  if (indexA < 0 || indexB < 0 || indexA >= cameras.length || indexB >= cameras.length) {
    debugWarn('[ViewActions] swapCameraSlots: index out of range', indexA, indexB, 'len', cameras.length);
    return;
  }
  const tmp = cameras[indexA];
  cameras[indexA] = cameras[indexB];
  cameras[indexB] = tmp;
  dispatch({ type: ASSIGN_CAMERA_TO_SLOT, payload: cameras });
  dispatch({ type: SET_VIEW_DIRTY, payload: true });
  debugLog('[ViewActions] swapped slots', indexA, '<->', indexB, '(view dirty)');
};

/**
 * Bulk-replace the whole camera arrangement (#86bb5dx62 Arrange mode: New-view blank
 * canvas + discard-restore). markDirty=false restores to a pristine (clean) state.
 */
export const setCameraArrangement = (cameras, markDirty = true) => (dispatch) => {
  dispatch({ type: ASSIGN_CAMERA_TO_SLOT, payload: cameras });
  if (markDirty) dispatch({ type: SET_VIEW_DIRTY, payload: true });
  else dispatch({ type: CLEAR_VIEW_DIRTY });
};

/**
 * Mark view as dirty (has unsaved changes)
 */
export const setViewDirty = (isDirty = true) => dispatch => {
  dispatch({ type: SET_VIEW_DIRTY, payload: isDirty });
};

/**
 * Revert the current view's unsaved edits back to the pristine (last-selected/saved) arrangement and
 * clear the dirty flag. Powers BOTH the "Undo" affordances (Views-panel banner + header dot) AND the
 * silent auto-revert when leaving playback / logging out — playback edits never require a save, so the
 * original view is always one step away. No prompt. If there's no pristine baseline, just clear dirty.
 * #86bb7phhh David 2026-08-13.
 */
export const revertViewToPristine = () => (dispatch, getState) => {
  const { pristineCameras } = getState().view || {};
  if (Array.isArray(pristineCameras) && pristineCameras.length) {
    dispatch(setCameraArrangement(pristineCameras.map((c) => ({ ...c })), false));
  } else {
    dispatch({ type: CLEAR_VIEW_DIRTY });
  }
  // Playback renders a LOCAL mirror of the arrangement (state.mcplayback.slots) so the session survives
  // navigation; the shared `cameras` just got reset, so RE-MATERIALIZE that mirror from it — otherwise the
  // grid keeps showing the discarded edit (Undo appeared to do nothing; nav-away-and-back kept the added
  // cam while "dirty" read clean). Both Undo and the auto-revert-on-leave route through here, so this is
  // the ONE place the shared array + the rendered mirror stay in sync. Mirrors loadViewIntoMcpb's slot
  // materialization; the downstream camSetKey effect refetches events. Harmless in Live (mcpb slots aren't
  // rendered there). #86bb7phhh David 2026-08-13.
  const after = getState();
  const currentCamView = after.camera && after.camera.currentCamView;
  const isEview = isMultiServerSession(after);
  const currentSerial = isEview ? (after.server.activeServerSerial || after.server.bSerial) : after.server.bSerial;
  const visible = getVisibleCameras(after.server.cameras, currentCamView);
  const slots = visible.map((c) => normalizeSlotCamera(c, { currentSerial }));
  dispatch({ type: SET_MCPB_SLOTS, payload: slots.length ? slots : [null] });
};

/**
 * Clear dirty flag (after save or discard)
 */
export const clearViewDirty = () => dispatch => {
  dispatch({ type: CLEAR_VIEW_DIRTY });
};

/**
 * Reset view selection (legacy function, kept for compatibility)
 */
export const resetView = () => dispatch => {
  dispatch({ type: SET_CURRENT_VIEW, payload: '' });
};
